import { useEffect, useState } from "react";
import { apiPost, apiPatch } from "../../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import ModuleChecklist from "./ModuleChecklist";

const empty = { name: "", contact_email: "", status: "active", modules: [] };

export default function OrgDialog({ open, onOpenChange, org, onSaved }) {
  const editing = !!org;
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setErr("");
      setForm(org
        ? { name: org.name, contact_email: org.contact_email || "", status: org.status, modules: org.modules || [] }
        : empty);
    }
  }, [open, org]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setErr("");
    if (!form.name.trim()) return setErr("Organization name is required.");
    setBusy(true);
    try {
      if (editing) {
        await apiPatch(`/admin/organizations/${org.id}`, {
          name: form.name, contact_email: form.contact_email, status: form.status, modules: form.modules,
        });
      } else {
        await apiPost("/admin/organizations", {
          name: form.name, contact_email: form.contact_email, modules: form.modules,
        });
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save organization.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="org-dialog">
        <DialogHeader>
          <DialogTitle className="small-caps">{editing ? "Edit organization" : "New organization"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="org-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Contact email</Label>
            <Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="org-email" />
          </div>
          {editing && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="org-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Enabled modules</Label>
            <ModuleChecklist value={form.modules} onChange={(v) => set("modules", v)} testidPrefix="org-mod" />
          </div>
          {err && <div className="text-[13px]" style={{ color: "var(--cv-crimson)" }} data-testid="org-error">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="org-save"
            style={{ background: "var(--cv-crimson)", color: "#fff" }}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
